// Browser-side runner for the public test demo. Uses fetch() against the
// /test/api/v1/* proxy on the same origin — credentials never reach the
// browser. Each step pushes structured events into the supplied console.

export type ConsoleLevel = 'info' | 'request' | 'response' | 'error' | 'success'

export interface ConsoleEvent {
  level: ConsoleLevel
  ts: number
  text: string
  detail?: string
}

export type ConsolePush = (event: Omit<ConsoleEvent, 'ts'>) => void

export interface RunResult {
  ok: boolean
  steps: { name: string; ok: boolean; durationMs: number; error?: string }[]
}

export interface ScenarioOptions {
  basePath: string
  push: ConsolePush
  signal?: AbortSignal
}

interface ApiResponse<T> {
  data: T
  meta?: { timestamp: string }
}

async function call<T>(opts: ScenarioOptions, method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  const url = `${opts.basePath}${path}`
  opts.push({ level: 'request', text: `${method} ${path}`, detail: body !== undefined ? JSON.stringify(body, null, 2) : undefined })

  const init: RequestInit = {
    method,
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  }
  const response = await fetch(url, init)
  const text = await response.text()
  let parsed: unknown
  try {
    parsed = text.length > 0 ? JSON.parse(text) : null
  } catch {
    parsed = text
  }
  opts.push({
    level: response.ok ? 'response' : 'error',
    text: `${response.status} ${response.statusText}`,
    detail: typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed),
  })
  if (!response.ok) {
    const detail = typeof parsed === 'object' && parsed !== null && 'detail' in parsed ? String((parsed as { detail: unknown }).detail) : text
    throw new Error(`${method} ${path} → ${response.status}: ${detail}`)
  }
  return parsed as T
}

export async function runTier1Scenario(opts: ScenarioOptions): Promise<RunResult> {
  const suffix = Math.random().toString(36).slice(2, 8)
  const roomId = `demo-${suffix}`
  const participantId = `demo-alice-${suffix}`
  const participantHeader = { 'X-Participant-Id': participantId }

  opts.push({ level: 'info', text: `Generated room id "${roomId}" and participant id "${participantId}".` })

  type StepFn = () => Promise<void>
  const steps: { name: string; fn: StepFn }[] = [
    {
      name: 'GET /status',
      fn: async () => {
        await call<ApiResponse<{ organization: string; application: string }>>(opts, 'GET', '/status')
      },
    },
    {
      name: 'POST /credentials',
      fn: async () => {
        await call<ApiResponse<{ ice_servers: unknown[] }>>(opts, 'POST', '/credentials', { ttl: 3600 })
      },
    },
    {
      name: `POST /rooms/${roomId}/join`,
      fn: async () => {
        await call<ApiResponse<{ ws_token: string }>>(opts, 'POST', `/rooms/${encodeURIComponent(roomId)}/join`, { display_name: 'Demo visitor', room_type: 'group' }, participantHeader)
      },
    },
    {
      name: `GET /rooms/${roomId}`,
      fn: async () => {
        await call<ApiResponse<unknown>>(opts, 'GET', `/rooms/${encodeURIComponent(roomId)}`, undefined, participantHeader)
      },
    },
    {
      name: `GET /rooms/${roomId}/participants`,
      fn: async () => {
        await call<ApiResponse<{ count: number }>>(opts, 'GET', `/rooms/${encodeURIComponent(roomId)}/participants`, undefined, participantHeader)
      },
    },
    {
      name: `POST /rooms/${roomId}/messages`,
      fn: async () => {
        await call<{ data: { message_id: string } }>(opts, 'POST', `/rooms/${encodeURIComponent(roomId)}/messages`, {
          message_type: 'text',
          content: 'Hello from the live demo!',
          client_message_id: `demo-${suffix}-1`,
        }, participantHeader)
      },
    },
    {
      name: `GET /rooms/${roomId}/messages`,
      fn: async () => {
        await call<{ data: { messages: unknown[] } }>(opts, 'GET', `/rooms/${encodeURIComponent(roomId)}/messages?limit=10`, undefined, participantHeader)
      },
    },
    {
      name: `POST /rooms/${roomId}/leave`,
      fn: async () => {
        await call<ApiResponse<{ remaining_participants: number }>>(opts, 'POST', `/rooms/${encodeURIComponent(roomId)}/leave`, undefined, participantHeader)
      },
    },
  ]

  const results: RunResult['steps'] = []
  for (const step of steps) {
    const started = performance.now()
    try {
      await step.fn()
      results.push({ name: step.name, ok: true, durationMs: Math.round(performance.now() - started) })
      opts.push({ level: 'success', text: `✓ ${step.name}` })
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      results.push({ name: step.name, ok: false, durationMs: Math.round(performance.now() - started), error })
      // Best-effort cleanup if a non-leave step failed.
      try {
        await call(opts, 'POST', `/rooms/${encodeURIComponent(roomId)}/leave`, undefined, participantHeader)
      } catch {
        // ignore
      }
      return { ok: false, steps: results }
    }
  }
  return { ok: true, steps: results }
}
